import { COUNTRY_COORDINATES } from '../models/gdeltModels';
import { batchGeocodeAddresses, geocodeAddress } from './geocoding';
import { normalizeWatchlistItemsForMap } from './watchlistMapModel';

const parseCoord = (value) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const hasValidCoordinates = (lat, lng) => {
  const la = parseCoord(lat);
  const ln = parseCoord(lng);
  if (la == null || ln == null) return false;
  return la >= -90 && la <= 90 && ln >= -180 && ln <= 180;
};

/**
 * Parse WKT-style Point strings from tenant node rows, e.g. Point(36.7889, -119.7901).
 * Supports both lat,lng and lng,lat order via magnitude heuristic.
 */
export const parsePointLocation = (location) => {
  if (location == null || location === '') return null;

  if (typeof location === 'object' && Array.isArray(location.coordinates)) {
    const [a, b] = location.coordinates;
    return normalizeLatLngPair(parseCoord(a), parseCoord(b));
  }

  const text = String(location).trim();
  const match = text.match(/point\s*\(\s*([-\d.]+)\s*[, ]\s*([-\d.]+)\s*\)/i);
  if (!match) return null;

  return normalizeLatLngPair(parseCoord(match[1]), parseCoord(match[2]));
};

const normalizeLatLngPair = (a, b) => {
  if (a == null || b == null) return null;

  if (hasValidCoordinates(a, b)) {
    return { lat: a, lng: b };
  }
  if (hasValidCoordinates(b, a)) {
    return { lat: b, lng: a };
  }

  if (Math.abs(a) <= 90 && Math.abs(b) <= 180 && Math.abs(b) > 90) {
    return { lat: a, lng: b };
  }
  if (Math.abs(b) <= 90 && Math.abs(a) <= 180 && Math.abs(a) > 90) {
    return { lat: b, lng: a };
  }
  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) {
    return Math.abs(a) <= Math.abs(b) ? { lat: a, lng: b } : { lat: b, lng: a };
  }

  return null;
};

export const getFacilityDisplayName = (row) =>
  row?.facility_name || row?.name || row?.facility_id || row?.address_id || '';

export const getSupplierTickerFromRow = (row) =>
  String(row?.ticker || row?.supplier_ticker || row?.supplier_id || row?.supplierId || '').trim();

/** @deprecated Use getSupplierTickerFromRow */
export const getSupplierIdFromRow = getSupplierTickerFromRow;

export const extractCoordinatesFromRow = (row) => {
  const explicitLat = parseCoord(row?.latitude ?? row?.lat);
  const explicitLng = parseCoord(row?.longitude ?? row?.lng ?? row?.lon);
  if (hasValidCoordinates(explicitLat, explicitLng)) {
    return { lat: explicitLat, lng: explicitLng, source: 'explicit' };
  }

  const fromLocation = parsePointLocation(row?.location);
  if (fromLocation) {
    return { ...fromLocation, source: 'location' };
  }

  return null;
};

/** Immediate coordinates for map markers (explicit, WKT, or country centroid). */
export const resolveCoordinatesFromRowSync = (row) => {
  const embedded = extractCoordinatesFromRow(row);
  if (embedded) return embedded;

  const countryOnly = resolveCountryCentroid(row?.country);
  if (countryOnly) {
    return { lat: countryOnly.lat, lng: countryOnly.lng, source: 'country' };
  }

  return null;
};

const buildAddressQuery = (row) => {
  const address = String(row?.street_address || row?.address || '').trim();
  const city = String(row?.city || '').trim();
  const country = String(row?.country || '').trim();
  const parts = [];
  if (address) parts.push(address);
  if (city && !address.toLowerCase().includes(city.toLowerCase())) parts.push(city);
  if (country && !address.toLowerCase().includes(country.toLowerCase())) parts.push(country);
  return parts.join(', ').trim();
};

export const rowToCoordinateKey = (row) => {
  const parts = [
    row?.street_address,
    row?.address,
    row?.city,
    row?.country,
    row?.origin,
    row?.destination,
    row?.facility_name,
  ]
    .filter(Boolean)
    .map((p) => String(p).trim().toLowerCase())
    .join('|');
  return parts || null;
};

export const resolveCountryCentroid = (country) => {
  if (!country) return null;
  const trimmed = String(country).trim();
  const direct = COUNTRY_COORDINATES[trimmed];
  if (direct) return { lat: direct.lat, lng: direct.lng };
  const lower = trimmed.toLowerCase();
  const match = Object.entries(COUNTRY_COORDINATES).find(
    ([name]) => name.toLowerCase() === lower
  );
  return match ? { lat: match[1].lat, lng: match[1].lng } : null;
};

export const resolveCoordinatesFromRow = async (row, options = {}) => {
  const embedded = extractCoordinatesFromRow(row);
  if (embedded) {
    return embedded;
  }

  const query = options.preferAddress
    ? buildAddressQuery(row)
    : [row?.origin, row?.destination, getFacilityDisplayName(row), row?.city, row?.country]
        .filter(Boolean)
        .join(', ')
        .trim();

  if (query) {
    const geocoded = await geocodeAddress(query);
    if (geocoded) return { ...geocoded, source: 'geocode' };
  }

  const countryOnly = resolveCountryCentroid(row?.country);
  if (countryOnly) return { ...countryOnly, source: 'country' };

  return null;
};

const facilityMarkerId = (row, index, prefix) =>
  `${prefix}-${row?.facility_id || row?.id || getFacilityDisplayName(row) || index}`;

const buildFacilityMarker = (row, index, type, nodeKey) => {
  const coords = resolveCoordinatesFromRowSync(row);
  if (!coords || !hasValidCoordinates(coords.lat, coords.lng)) return null;

  const isSupplier = type === 'supplier';
  const title =
    getFacilityDisplayName(row) ||
    (isSupplier ? getSupplierTickerFromRow(row) : null) ||
    row?.facility_id ||
    row?.id ||
    (isSupplier ? `Supplier site ${index + 1}` : `Facility ${index + 1}`);

  return {
    id: facilityMarkerId(row, index, isSupplier ? 'supplier' : 'own'),
    type,
    position: [coords.lat, coords.lng],
    title,
    subtitle: [
      isSupplier ? getSupplierTickerFromRow(row) : null,
      row?.facility_type || row?.type,
      row?.city,
      row?.country,
    ]
      .filter(Boolean)
      .join(' · '),
    row,
    nodeKey,
    rowIndex: index,
    coordinateSource: coords.source,
    naturalHazardScore: row?.natural_hazard_score ?? row?.naturalHazardScore,
    operationalStatus: row?.operational_status ?? row?.operationalStatus,
    supplierTicker: isSupplier ? getSupplierTickerFromRow(row) : undefined,
  };
};

export const buildStaticMapMarkers = (nodesByKey = {}) => {
  const markers = [];
  const facilities = Array.isArray(nodesByKey.facilities) ? nodesByKey.facilities : [];
  const supplierFacilities = Array.isArray(nodesByKey.supplierFacilities)
    ? nodesByKey.supplierFacilities
    : [];

  facilities.forEach((row, index) => {
    const marker = buildFacilityMarker(row, index, 'own', 'facilities');
    if (marker) markers.push(marker);
  });

  supplierFacilities.forEach((row, index) => {
    const marker = buildFacilityMarker(row, index, 'supplier', 'supplierFacilities');
    if (marker) markers.push(marker);
  });

  return markers;
};

const readRiskScoreFromEntry = (entry) => {
  const score =
    entry?.company?.overallRiskScore ??
    entry?.company?.overall_risk_score ??
    entry?.userCompanyInfo?.overallRiskScore ??
    entry?.userCompanyInfo?.overall_risk_score ??
    entry?.overallRiskScore ??
    entry?.overall_risk_score ??
    entry?.latestRiskAnalysis?.overallRiskScore ??
    entry?.latestRiskAnalysis?.overall_risk_score;
  const value = Number(score);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const readTickerFromEntry = (entry) =>
  String(
    entry?.companyId ??
      entry?.company_id ??
      entry?.ticker ??
      entry?.supplier_ticker ??
      entry?.supplierTicker ??
      ''
  ).trim();

export const buildTenantRiskByTicker = (items = []) => {
  const riskByTicker = {};
  const normalized = normalizeWatchlistItemsForMap(items);
  const rows = normalized.length > 0 ? normalized : Array.isArray(items) ? items : [];

  rows.forEach((entry) => {
    const ticker = readTickerFromEntry(entry);
    if (!ticker) return;
    const score = readRiskScoreFromEntry(entry);
    if (score != null) {
      riskByTicker[ticker] = { overallRiskScore: score };
    }
  });
  return riskByTicker;
};

export const mergeMapMarkers = (...markerGroups) => {
  const byId = new Map();
  markerGroups.flat().forEach((marker) => {
    if (marker?.id) byId.set(marker.id, marker);
  });
  return Array.from(byId.values());
};

export const mergeMapRoutes = (...routeGroups) => {
  const byId = new Map();
  routeGroups.flat().forEach((route) => {
    if (route?.id) byId.set(route.id, route);
  });
  return Array.from(byId.values());
};

export const buildGeoRegionMarkers = (nodesByKey = {}) => {
  const geographies = Array.isArray(nodesByKey.geographies) ? nodesByKey.geographies : [];
  return geographies
    .map((row, index) => {
      const centroid = resolveCountryCentroid(row?.country);
      if (!centroid) return null;
      return {
        id: `geo-${index}-${row?.country}`,
        type: 'geography',
        position: [centroid.lat, centroid.lng],
        title: [row?.city, row?.country].filter(Boolean).join(', ') || row?.country,
        subtitle: [row?.region, row?.risk_level ? `Risk: ${row.risk_level}` : null]
          .filter(Boolean)
          .join(' · '),
        row,
        nodeKey: 'geographies',
        rowIndex: index,
        riskLevel: row?.risk_level,
      };
    })
    .filter(Boolean);
};

export const buildCoordinateLookup = (markers = []) => {
  const lookup = new Map();
  markers.forEach((marker) => {
    const row = marker.row || {};
    const keys = [
      row.facility_id,
      row.facility_name,
      row.facility_id && row.facility_name ? `${row.facility_id}:${row.facility_name}` : null,
      marker.title,
    ]
      .filter(Boolean)
      .map((k) => String(k).trim().toLowerCase());
    keys.forEach((key) => {
      if (!lookup.has(key)) {
        lookup.set(key, marker.position);
      }
    });
    const addressKey = rowToCoordinateKey(row);
    if (addressKey && !lookup.has(addressKey)) {
      lookup.set(addressKey, marker.position);
    }
  });
  return lookup;
};

export const scoreToRiskLevelKey = (score) => {
  const value = Number(score);
  if (!Number.isFinite(value) || value <= 0) return 'unknown';
  if (value >= 7) return 'high';
  if (value >= 4) return 'medium';
  return 'low';
};

export const getMarkerLocationLabel = (marker) => {
  const row = marker?.row || {};
  const cityState = [row.city, row.state ?? row.stateProvince ?? row.state_province]
    .filter(Boolean)
    .join(', ');
  const parts = [cityState || row.city, row.country].filter(Boolean);
  if (parts.length > 0) return parts.join(', ');
  const address = row.street_address || row.address || row.streetAddress;
  if (address) return String(address).trim();
  return String(marker?.subtitle || '').trim();
};

export const getMarkerBreadcrumb = (marker, fallbackName = '') => {
  const location = getMarkerLocationLabel(marker);
  const name = String(marker?.title || fallbackName || '').trim();
  return {
    location: location || name,
    name: name || location,
  };
};

export const enrichMarkersWithSupplierRisk = (
  markers = [],
  tenantSuppliers = [],
  companies = []
) => {
  const riskByTicker = {
    ...buildTenantRiskByTicker(companies),
    ...buildTenantRiskByTicker(tenantSuppliers),
  };
  return markers.map((marker) => {
    const ticker = marker.supplierTicker || getSupplierTickerFromRow(marker.row);
    const risk = ticker ? riskByTicker[ticker]?.overallRiskScore : null;
    if (risk == null) return marker;
    return { ...marker, riskScore: risk };
  });
};

export const buildHighRiskSupplierMarkers = (markers = [], { minScore = 7, limit = 12 } = {}) => {
  const suppliers = markers
    .filter((marker) => marker.type === 'supplier')
    .map((marker) => ({
      ...marker,
      riskScore: Number(marker.riskScore) > 0 ? Number(marker.riskScore) : 0,
    }))
    .sort((a, b) => b.riskScore - a.riskScore);

  const highRisk = suppliers.filter((marker) => marker.riskScore >= minScore);
  return (highRisk.length > 0 ? highRisk : suppliers).slice(0, limit);
};

export const buildTenantNetworkRoutes = (markers = [], companyName = '') => {
  const ownMarkers = markers.filter((marker) => marker.type === 'own');
  const supplierMarkers = markers.filter((marker) => marker.type === 'supplier');
  if (ownMarkers.length === 0 || supplierMarkers.length === 0) return [];

  const routes = [];
  ownMarkers.forEach((own) => {
    supplierMarkers.forEach((supplier) => {
      routes.push({
        id: `network-${own.id}-${supplier.id}`,
        type: 'network',
        positions: [own.position, supplier.position],
        title: supplier.title,
        subtitle: companyName,
        fromMarkerId: own.id,
        toMarkerId: supplier.id,
      });
    });
  });
  return routes;
};

export const buildStaticMapRoutes = (nodesByKey = {}, coordinateLookup = new Map()) => {
  const routes = [];
  const logistics = Array.isArray(nodesByKey.logistics) ? nodesByKey.logistics : [];
  const graph = Array.isArray(nodesByKey.graphRelationships) ? nodesByKey.graphRelationships : [];

  const resolveEndpoint = (label) => {
    if (!label) return null;
    const key = String(label).trim().toLowerCase();
    return coordinateLookup.get(key) || null;
  };

  logistics.forEach((row, index) => {
    const start =
      resolveEndpoint(row?.origin) ||
      coordinateLookup.get(rowToCoordinateKey({ origin: row?.origin, city: row?.origin }));
    const end =
      resolveEndpoint(row?.destination) ||
      coordinateLookup.get(rowToCoordinateKey({ destination: row?.destination, city: row?.destination }));
    if (!start || !end) return;
    routes.push({
      id: `logistics-${index}`,
      type: 'logistics',
      positions: [start, end],
      title: row?.route_name || row?.origin || `Route ${index + 1}`,
      subtitle: [row?.mode, row?.lead_time_days ? `${row.lead_time_days}d` : null]
        .filter(Boolean)
        .join(' · '),
      row,
    });
  });

  graph.forEach((row, index) => {
    const start = resolveEndpoint(row?.source);
    const end = resolveEndpoint(row?.target);
    if (!start || !end) return;
    routes.push({
      id: `graph-${index}`,
      type: 'graph',
      positions: [start, end],
      title: row?.relationship_type || 'Relationship',
      subtitle: [row?.source, row?.target].filter(Boolean).join(' → '),
      row,
    });
  });

  return routes;
};

const buildGeocodeQueryForRow = (row) => {
  const addressQuery = buildAddressQuery(row);
  if (addressQuery) return addressQuery;
  return [row?.origin, row?.destination, getFacilityDisplayName(row), row?.city, row?.country]
    .filter(Boolean)
    .join(', ')
    .trim();
};

export const enrichMarkersWithGeocoding = async (nodesByKey = {}, existingMarkers = []) => {
  const lookup = buildCoordinateLookup(existingMarkers);
  const pending = [];

  const facilities = Array.isArray(nodesByKey.facilities) ? nodesByKey.facilities : [];
  facilities.forEach((row, index) => {
    if (extractCoordinatesFromRow(row)) return;
    pending.push({ row, index, type: 'own', nodeKey: 'facilities' });
  });

  const supplierFacilities = Array.isArray(nodesByKey.supplierFacilities)
    ? nodesByKey.supplierFacilities
    : [];
  supplierFacilities.forEach((row, index) => {
    if (extractCoordinatesFromRow(row)) return;
    pending.push({ row, index, type: 'supplier', nodeKey: 'supplierFacilities' });
  });

  const extraMarkers = [];
  const geocodeQueue = [];

  pending.forEach((item) => {
    const countryCoords = resolveCountryCentroid(item.row?.country);
    if (countryCoords) {
      const { row, index, type, nodeKey } = item;
      extraMarkers.push({
        id: facilityMarkerId(row, index, type === 'supplier' ? 'supplier' : 'own'),
        type,
        position: [countryCoords.lat, countryCoords.lng],
        title:
          getFacilityDisplayName(row) ||
          getSupplierTickerFromRow(row) ||
          (type === 'supplier' ? `Supplier site ${index + 1}` : `Facility ${index + 1}`),
        subtitle: [row?.facility_type || row?.type, row?.city, row?.country].filter(Boolean).join(' · '),
        row,
        nodeKey,
        rowIndex: index,
        geocoded: true,
        coordinateSource: 'country',
      });
      const addressKey = rowToCoordinateKey(row);
      if (addressKey) lookup.set(addressKey, [countryCoords.lat, countryCoords.lng]);
      return;
    }

    const query = buildGeocodeQueryForRow(item.row);
    if (query) {
      geocodeQueue.push({ item, query });
    }
  });

  if (geocodeQueue.length > 0) {
    const uniqueQueries = [...new Set(geocodeQueue.map((entry) => entry.query))];
    const geocodedByQuery = await batchGeocodeAddresses(uniqueQueries);

    geocodeQueue.forEach(({ item, query }) => {
      const coords = geocodedByQuery.get(query);
      if (!coords) return;
      const { row, index, type, nodeKey } = item;
      extraMarkers.push({
        id: facilityMarkerId(row, index, type === 'supplier' ? 'supplier' : 'own'),
        type,
        position: [coords.lat, coords.lng],
        title:
          getFacilityDisplayName(row) ||
          getSupplierTickerFromRow(row) ||
          (type === 'supplier' ? `Supplier site ${index + 1}` : `Facility ${index + 1}`),
        subtitle: [row?.facility_type || row?.type, row?.city, row?.country].filter(Boolean).join(' · '),
        row,
        nodeKey,
        rowIndex: index,
        geocoded: true,
        coordinateSource: 'geocode',
      });
      const addressKey = rowToCoordinateKey(row);
      if (addressKey) lookup.set(addressKey, [coords.lat, coords.lng]);
    });
  }

  return [...existingMarkers, ...extraMarkers];
};

export const computeMapBounds = (markers = [], routes = []) => {
  const points = [];
  markers.forEach((m) => points.push(m.position));
  routes.forEach((r) => {
    (r.positions || []).forEach((p) => points.push(p));
  });
  return points;
};

export const computeProfileCompletion = (profile = {}, nodesByKey = {}) => {
  const fields = [
    'name',
    'legal_name',
    'duns_number',
    'country',
    'city',
    'industry',
    'annual_revenue',
    'employee_count',
    'stock_ticker',
  ];
  const filled = fields.filter((key) => String(profile?.[key] ?? '').trim().length > 0).length;
  const hasGeo =
    buildStaticMapMarkers(nodesByKey).length > 0 ||
    buildGeoRegionMarkers(nodesByKey).length > 0 ||
    (Array.isArray(nodesByKey.supplierFacilities) && nodesByKey.supplierFacilities.length > 0);
  const base = Math.round((filled / fields.length) * (hasGeo ? 85 : 100));
  return Math.min(100, hasGeo ? base + 15 : base);
};

export const computeCompletionChecklist = (profile = {}, nodesByKey = {}) => {
  const hasName = String(profile?.name || '').trim().length > 0;
  const hasTicker = String(profile?.stock_ticker || '').trim().length > 0;
  const facilityCount = Array.isArray(nodesByKey.facilities) ? nodesByKey.facilities.length : 0;
  const geoCount = Array.isArray(nodesByKey.geographies) ? nodesByKey.geographies.length : 0;
  const hasLocatedFacility = (nodesByKey.facilities || []).some((row) =>
    hasValidCoordinates(row?.latitude ?? row?.lat, row?.longitude ?? row?.lng ?? row?.lon)
  );

  return [
    { id: 'name', done: hasName, targetPrimary: 'overview', targetDataSection: null },
    { id: 'ticker', done: hasTicker, targetPrimary: 'overview', targetDataSection: null },
    {
      id: 'facilities',
      done: hasLocatedFacility || facilityCount > 0,
      targetPrimary: 'data',
      targetDataSection: 'import',
    },
    {
      id: 'geographies',
      done: geoCount > 0,
      targetPrimary: 'data',
      targetDataSection: 'geographies',
    },
  ];
};

export const computeNodeStats = (nodesByKey = {}) => ({
  facilities: Array.isArray(nodesByKey.facilities) ? nodesByKey.facilities.length : 0,
  products: Array.isArray(nodesByKey.products) ? nodesByKey.products.length : 0,
  logistics: Array.isArray(nodesByKey.logistics) ? nodesByKey.logistics.length : 0,
  geographies: Array.isArray(nodesByKey.geographies) ? nodesByKey.geographies.length : 0,
  commodities: Array.isArray(nodesByKey.commodities) ? nodesByKey.commodities.length : 0,
  supplierFacilities: Array.isArray(nodesByKey.supplierFacilities)
    ? nodesByKey.supplierFacilities.length
    : 0,
  graphRelationships: Array.isArray(nodesByKey.graphRelationships)
    ? nodesByKey.graphRelationships.length
    : 0,
});

/** Build display fields for the map detail sidebar from a marker. */
export const buildTenantMapMarkerDetails = (marker, t) => {
  if (!marker) return [];

  const row = marker.row || {};
  const fields = [];

  const push = (labelKey, value) => {
    const text = value == null ? '' : String(value).trim();
    if (text) fields.push({ label: t(labelKey), value: text });
  };

  push('tenantMapDetailType', t(`tenantMapMarkerType_${marker.type}`));

  if (marker.type === 'geography') {
    push('tenantMapDetailCountry', row.country || marker.title);
    push('tenantMapDetailRegion', row.region);
    push('tenantMapDetailRiskLevel', marker.riskLevel || row.risk_level);
    return fields;
  }

  if (marker.companyId) {
    push('tenantMapDetailSupplier', marker.title);
    push('tenantMapDetailTicker', marker.companyId);
    if (marker.riskScore != null && marker.riskScore > 0) {
      push('tenantMapDetailRiskScore', Math.round(marker.riskScore));
    }
    push('tenantMapDetailAddress', marker.subtitle);
    return fields;
  }

  push('tenantMapDetailName', getFacilityDisplayName(row) || marker.title);
  if (marker.type === 'supplier') {
    push('tenantMapDetailTicker', getSupplierTickerFromRow(row) || marker.supplierTicker);
  }
  push('tenantMapDetailAddress', row.street_address || row.address || row.streetAddress);
  push('tenantMapDetailCity', row.city);
  push('tenantMapDetailCountry', row.country);
  push('tenantMapDetailFacilityType', row.facility_type || row.facilityType || row.type);
  push('tenantMapDetailOperationalStatus', marker.operationalStatus);
  if (marker.naturalHazardScore != null && marker.naturalHazardScore !== '') {
    push('tenantMapDetailNaturalHazard', marker.naturalHazardScore);
  }
  if (marker.riskScore != null && marker.riskScore > 0) {
    push('tenantMapDetailRiskScore', Math.round(marker.riskScore));
  }

  return fields;
};
